import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJSONSource, LngLatLike, Map, Marker } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface CityNode {
  id: string;
  name: string;
  coordinates: [number, number];
  activities: string[];
}

const CITY_NODES: CityNode[] = [
  {
    id: 'tampere',
    name: 'Tampere',
    coordinates: [23.761025, 61.497753],
    activities: ['Siella.fi - Erasmus internship'],
  },
  {
    id: 'praha',
    name: 'Prague',
    coordinates: [14.4378, 50.0755],
    activities: ['Tiimiakatemia', 'Web dev', 'Vodafone'],
  },
  {
    id: 'madrid',
    name: 'Madrid',
    coordinates: [-3.7038, 40.4168],
    activities: ['TeamLabs Erasmus'],
  },
];

type LinePath = [number, number][];

const createBentPath = (from: [number, number], to: [number, number], bend = 2.4): LinePath => {
  const midLng = (from[0] + to[0]) / 2;
  const midLat = (from[1] + to[1]) / 2;

  return [
    from,
    [midLng - 1.2, midLat + bend],
    [midLng + 0.8, midLat - bend / 1.8],
    to,
  ];
};

const createSequentialConnections = (cities: CityNode[]): LinePath[] => {
  const connections: LinePath[] = [];

  for (let i = 0; i < cities.length - 1; i += 1) {
    const from = cities[i].coordinates;
    const to = cities[i + 1].coordinates;
    const bendStrength = 2.8;
    connections.push(createBentPath(from, to, bendStrength));
  }

  return connections;
};

const calculateLength = (path: LinePath): number => {
  let total = 0;
  for (let i = 0; i < path.length - 1; i += 1) {
    const [x1, y1] = path[i];
    const [x2, y2] = path[i + 1];
    total += Math.hypot(x2 - x1, y2 - y1);
  }
  return total;
};

const pointAtProgress = (path: LinePath, progress: number): [number, number] => {
  const totalLength = calculateLength(path);
  if (totalLength === 0) {
    return path[0];
  }

  const normalizedProgress = ((progress % 1) + 1) % 1;
  let distanceLeft = totalLength * normalizedProgress;

  for (let i = 0; i < path.length - 1; i += 1) {
    const [x1, y1] = path[i];
    const [x2, y2] = path[i + 1];
    const segmentLength = Math.hypot(x2 - x1, y2 - y1);
    if (distanceLeft <= segmentLength) {
      const ratio = segmentLength === 0 ? 0 : distanceLeft / segmentLength;
      return [x1 + (x2 - x1) * ratio, y1 + (y2 - y1) * ratio];
    }
    distanceLeft -= segmentLength;
  }

  return path[path.length - 1];
};

const ActivityMap: React.FC = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const markersRef = useRef<Marker[]>([]);
  const markerElementsRef = useRef<Record<string, HTMLButtonElement>>({});
  const animationFrameRef = useRef<number | null>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const [selectedCity, setSelectedCity] = useState<CityNode>(CITY_NODES[0]);
  const [isMobileInfoVisible, setIsMobileInfoVisible] = useState(true);

  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  const paths = useMemo<LinePath[]>(() => createSequentialConnections(CITY_NODES), []);

  useEffect(() => {
    if (!sectionRef.current || shouldLoadMap) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '300px 0px',
      }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadMap]);

  const updateMarkerStyles = (activeId: string) => {
    Object.entries(markerElementsRef.current).forEach(([cityId, element]) => {
      const isActive = cityId === activeId;
      element.style.transform = isActive ? 'scale(1.45)' : 'scale(1)';
      element.style.boxShadow = isActive
        ? '0 0 0 4px rgba(63,224,208,0.35), 0 0 20px rgba(63,224,208,0.65)'
        : '0 0 0 3px rgba(63,224,208,0.2)';

      const pulse = element.querySelector('span');
      if (pulse) {
        pulse.style.opacity = isActive ? '1' : '0.15';
      }
    });
  };

  useEffect(() => {
    updateMarkerStyles(selectedCity.id);
  }, [selectedCity]);

  useEffect(() => {
    if (!token || !shouldLoadMap || !mapContainerRef.current || mapRef.current) {
      return;
    }

    let isCancelled = false;
    let map: Map | null = null;

    const initMap = async () => {
      const { default: mapboxgl } = await import('mapbox-gl');

      if (isCancelled || !mapContainerRef.current || mapRef.current) {
        return;
      }

      mapboxgl.accessToken = token;
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [11.2, 50.2] as LngLatLike,
        zoom: 3.25,
        pitch: 25,
        bearing: -8,
        antialias: true,
      });
      mapRef.current = map;

      map.on('load', () => {
        const lineFeatures = paths.map((path, index) => ({
          type: 'Feature' as const,
          id: `line-${index}`,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: path,
          },
        }));

        map?.addSource('activity-lines', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: lineFeatures,
          },
        });

        map?.addLayer({
          id: 'activity-lines-layer',
          type: 'line',
          source: 'activity-lines',
          paint: {
            'line-color': '#3fe0d0',
            'line-width': 2.4,
            'line-opacity': 0.7,
            'line-blur': 0.2,
            'line-dasharray': [1, 1.6, 0.2, 1.3],
          },
        });

        map?.addSource('activity-flow', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });

        map?.addLayer({
          id: 'activity-flow-layer',
          type: 'circle',
          source: 'activity-flow',
          paint: {
            'circle-radius': 4,
            'circle-color': '#3fe0d0',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#051018',
            'circle-opacity': 0.95,
          },
        });

        CITY_NODES.forEach((city) => {
          const markerElement = document.createElement('button');
          markerElement.className =
            'group relative h-4 w-4 rounded-full border-2 border-[#3fe0d0] bg-[#051018] transition-transform duration-200 hover:scale-125';
          markerElement.type = 'button';
          markerElement.setAttribute('aria-label', city.name);
          markerElement.onclick = () => {
            setSelectedCity(city);
            setIsMobileInfoVisible(true);
          };

          const pulse = document.createElement('span');
          pulse.className =
            'pointer-events-none absolute -inset-2 rounded-full border border-[#3fe0d0]/60 animate-ping';
          pulse.style.transition = 'opacity 180ms ease';
          markerElement.appendChild(pulse);

          const marker = new mapboxgl.Marker({ element: markerElement, anchor: 'center' })
            .setLngLat(city.coordinates)
            .addTo(map as Map);

          markerElementsRef.current[city.id] = markerElement;
          markersRef.current.push(marker);
        });

        updateMarkerStyles(selectedCity.id);

        const animate = (time: number) => {
          const source = map?.getSource('activity-flow') as GeoJSONSource | undefined;
          if (!source) {
            animationFrameRef.current = requestAnimationFrame(animate);
            return;
          }

          const speed = 0.00011;
          const baseProgress = time * speed;
          const features = paths.flatMap((path, pathIndex) => {
            const progressOffset = pathIndex * 0.19;
            return [0, 0.35, 0.7].map((phase, dotIndex) => {
              const point = pointAtProgress(path, baseProgress + phase + progressOffset);
              return {
                type: 'Feature' as const,
                properties: {
                  id: `${pathIndex}-${dotIndex}`,
                },
                geometry: {
                  type: 'Point' as const,
                  coordinates: point,
                },
              };
            });
          });

          source.setData({
            type: 'FeatureCollection',
            features,
          });

          animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);
      });
    };

    void initMap();

    return () => {
      isCancelled = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      markerElementsRef.current = {};
      map?.remove();
      mapRef.current = null;
    };
  }, [paths, shouldLoadMap, token]);

  return (
    <section ref={sectionRef} id="map" className="py-12 md:py-20 bg-surface">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-4xl md:text-6xl font-bold mb-8 md:mb-12">ACTIVITY MAP</h2>
        <p className="text-lg text-text/90 mb-8 border-l-4 border-accent pl-4">
          Click a node to see the activities in that city.
        </p>

        <div className="grid lg:grid-cols-[1.4fr_0.8fr] gap-6">
          <div className="border-3 border-accent/80 bg-background h-[460px] overflow-hidden relative">
            {token && shouldLoadMap ? (
              <div ref={mapContainerRef} className="w-full h-full" />
            ) : token ? (
              <div className="w-full h-full flex items-center justify-center text-center px-6">
                <p className="text-text/80">Map will load when this section becomes visible.</p>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-center px-6">
                <p className="text-text/80">
                  `REACT_APP_MAPBOX_ACCESS_TOKEN` is missing in `.env.local` or `.env.production`.
                </p>
              </div>
            )}

            <div className="absolute top-3 right-3 z-20 lg:hidden">
              <button
                type="button"
                className="brutalist-button !px-3 !py-2 text-xs"
                onClick={() => setIsMobileInfoVisible((prev) => !prev)}
              >
                {isMobileInfoVisible ? 'HIDE INFO' : 'SHOW INFO'}
              </button>
            </div>

            {isMobileInfoVisible && (
              <div className="absolute inset-x-3 bottom-3 z-20 border-3 border-surface-alt bg-background/95 p-4 shadow-brutal lg:hidden">
                <p className="text-xs uppercase tracking-[0.18em] text-accent mb-2">Selected node</p>
                <h3 className="text-2xl font-bold mb-3">{selectedCity.name}</h3>
                <ul className="space-y-2">
                  {selectedCity.activities.map((activity) => (
                    <li
                      key={activity}
                      className="border border-surface-alt bg-surface/70 px-3 py-2 text-sm text-text/95"
                    >
                      {activity}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="hidden lg:block border-3 border-surface-alt bg-background p-6 shadow-brutal">
            <p className="text-sm uppercase tracking-[0.22em] text-accent mb-3">Selected node</p>
            <h3 className="text-3xl font-bold mb-4">{selectedCity.name}</h3>
            <ul className="space-y-3">
              {selectedCity.activities.map((activity) => (
                <li
                  key={activity}
                  className="border border-surface-alt bg-surface/70 px-3 py-2 text-text/95"
                >
                  {activity}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivityMap;
